import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RESTORE_KIOSK_KEY, SESSION_KIOSK_KEY, isInstalledPwa } from '@/hooks/useAutoReloadOnUpdate';

/** «На весь экран» для дашборда на планшете (Алексей 20.10, #1610/#1611): прячем
 *  сайдбар и шапку приложения и просим у браузера настоящий fullscreen. Где
 *  Fullscreen API нет (iPhone Safari) — остаётся только скрытие хрома, что на
 *  установленной PWA и так даёт весь экран. Выход — та же кнопка или Esc/жест
 *  браузера: событие fullscreenchange возвращает хром на место. */
interface KioskValue {
  kiosk: boolean;
  setKiosk: (on: boolean) => void;
  toggleKiosk: () => void;
}

const KioskContext = createContext<KioskValue | null>(null);
const KIOSK_KEY = 'app:kiosk';

type FsDoc = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
};
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };

function fsElement(): Element | null {
  const d = document as FsDoc;
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

async function requestFs(): Promise<void> {
  const el = document.documentElement as FsEl;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
  } catch { /* отказ браузера (например, без жеста) — остаёмся в режиме без хрома */ }
}

async function exitFs(): Promise<void> {
  const d = document as FsDoc;
  try {
    if (!fsElement()) return;
    if (d.exitFullscreen) await d.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
  } catch { /* ignore */ }
}

export function KioskProvider({ children }: { children: React.ReactNode }) {
  // Режим НЕ переживает перезагрузку (Алексей 23.10 #2109): браузер при перезагрузке (жест «потянуть
  // вниз», автообновление) всегда выходит из fullscreen — запомненный режим прятал хром приложения
  // при видимой строке браузера. После автообновления вернуть его предлагает окно «Обновлено»
  // (кнопка «Развернуть на весь экран» — браузер даёт fullscreen только по жесту).
  // Исключение — установленная PWA (Алексей 03.11 #8583/#8585): строки браузера там нет, после
  // автообновления дашборд должен вернуться во весь экран сам, без кнопки — режим запоминаем.
  // И во вкладке браузера после перезагрузки ПО ОБНОВЛЕНИЮ (#8621): строка браузера вернулась, но
  // режим внутри приложения остаётся, а «Развернуть на весь экран» вернёт и fullscreen. Обычная
  // перезагрузка («потянуть вниз») режим по-прежнему сбрасывает.
  const [kiosk, setKioskState] = useState<boolean>(() => {
    try {
      if (isInstalledPwa()) return localStorage.getItem(KIOSK_KEY) === '1';
      const restore = sessionStorage.getItem(RESTORE_KIOSK_KEY) === '1';
      sessionStorage.removeItem(RESTORE_KIOSK_KEY);
      if (restore) sessionStorage.setItem(SESSION_KIOSK_KEY, '1');
      return restore;
    } catch { return false; }
  });
  const setKiosk = useCallback((on: boolean) => {
    setKioskState(on);
    try {
      if (isInstalledPwa()) localStorage.setItem(KIOSK_KEY, on ? '1' : '0');
      sessionStorage.setItem(SESSION_KIOSK_KEY, on ? '1' : '0');
    } catch { /* приватный режим */ }
    if (on) void requestFs();
    else void exitFs();
  }, []);
  const toggleKiosk = useCallback(() => setKiosk(!kiosk), [kiosk, setKiosk]);
  // Esc / системный выход из fullscreen → выходим и из режима, хром на место.
  useEffect(() => {
    const onChange = () => {
      if (fsElement()) return;
      setKioskState(false);
      try {
        if (isInstalledPwa()) localStorage.setItem(KIOSK_KEY, '0');
        sessionStorage.setItem(SESSION_KIOSK_KEY, '0');
      } catch { /* ignore */ }
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);
  const value = useMemo(() => ({ kiosk, setKiosk, toggleKiosk }), [kiosk, setKiosk, toggleKiosk]);
  return <KioskContext.Provider value={value}>{children}</KioskContext.Provider>;
}

export function useKiosk(): KioskValue {
  const ctx = useContext(KioskContext);
  // Вне Layout (например, страница логина) — режим недоступен, но хук не падает.
  return ctx ?? { kiosk: false, setKiosk: () => {}, toggleKiosk: () => {} };
}
